import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  getAccountChargesByUser,
  getAccountsByUser,
  getAllTradesForAnalytics,
  getTptWalletContext,
  getTradeTagsByUser,
  getTradingSessionsByUser,
  getTradingStrategiesByUser,
  useQuery,
} from "wasp/client/operations";
import type { EnrichedAccount } from "../../trading-accounts/accountTypes";
import { accountExpensesInPeriod, chargeAmountInPeriod } from "../../prop-firm-manager/pfmPeriod";
import { resolveBounds, resolveFloor, type AxisBounds } from "../../trading-accounts/accountProgressGeometry";
import type { AxisTone } from "../../trading-accounts/components/AccountProgressBar";
import { useLanguageContext } from "../../client/contexts/LanguageContext";
import { makeFormat, dayKey, type LiteFormat } from "../lib/format";
import { matchScope, inPeriod } from "../lib/filters";
import { toLiteTrade, type AccountSet, type LiteFilters, type LiteTrade } from "../lib/types";
import { matchesPhase } from "../pages/pfm/pfmModel";
import type { MoodKey } from "../lib/analytics";

export type AccountLike = EnrichedAccount & {
  createdAt?: string | Date | null;
  fundedAt?: string | Date | null;
  resetAt?: string | Date | null;
  purchasedAt?: string | Date | null;
  previousAccountId?: string | null;
  deactivatedReason?: string | null;
  /** Mémoire durable du statut funded (trigger Postgres) : survit à la clôture. */
  wasFunded?: boolean;
  targetReachedAt?: string | Date | null;
  propFirmAccountType?: { pricingModel?: string | null } | null;
};

export type LiteStatus = "challenge" | "funded" | "live" | "personal" | "demo" | "inactive" | "breached";

export interface LiteFirm { key: string; id: string; slug: string; name: string; color: string; textColor: string }

/** Compte vu par le Lite : l'état vient du moteur prop firm (serveur), jamais recalculé ici. */
export interface LiteAccount {
  id: string;
  name: string;
  raw: AccountLike;
  firm: LiteFirm | null;
  /** Clé de filtre : id du template, ou "personal". */
  firmKey: string;
  status: LiteStatus;
  active: boolean;
  balance: number;
  pnl: number;
  /** Progression vers l'objectif (challenge) ou vers le seuil de payout (funded). */
  target: { kind: "challenge" | "payout"; ratio: number; amount: number; current: number } | null;
  /** Marge avant la perte max, issue de bufferStatus. */
  cushion: { remaining: number; ratio: number; max: number; warning: boolean; violation: boolean } | null;
  /** Axe plancher -> cible de la carte Advanced (resolveBounds), ou null si une borne manque. */
  axis: { bounds: AxisBounds; tone: AxisTone } | null;
  payouts: { id: string; date: string; amount: number }[];
  payoutsTotal: number;
  expensesTotal: number;
  net: number;
}

export interface Charge {
  id: string;
  name: string;
  amount: number;
  frequency: string;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  billingDay?: number | null;
  createdAt?: string | Date | null;
  accountId?: string | null;
  account?: { id: string; name: string } | null;
  source?: string | null;
  cancelAskedAt?: string | Date | null;
  renewalConfirmedAt?: string | Date | null;
  /** Exceptions par échéance (AccountChargeOccurrence), statut non nul seulement. */
  occurrences?: { dueDate: string | Date; status: string | null; amount: number | null }[] | null;
}

const DEFAULT_FILTERS: LiteFilters = { period: "month", accountId: null, accountSet: null, firm: null, strategyId: null };
// v2 : `accountSet` peut valoir null (défaut de page). Une ancienne valeur "all"
// enregistrée passerait pour un choix explicite, d'où la nouvelle clé.
const FILTERS_KEY = "edgely:lite:filters:v2";

/**
 * Groupe de comptes par défaut, quand l'utilisateur n'a rien choisi. Comme
 * l'Advanced : les comptes actifs. Exception, le Prop Firm Manager : ses dépenses
 * et son ROI ont besoin des challenges cramés (le PFM Advanced les compte aussi).
 */
export function defaultAccountSet(pathname: string): AccountSet {
  return pathname.startsWith("/prop-firm-manager") ? "all" : "active";
}

function readFilters(): LiteFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
  } catch { /* stockage indisponible */ }
  return DEFAULT_FILTERS;
}

function toStatus(a: AccountLike): LiteStatus {
  const hasFirm = !!(a.effectiveTemplate ?? a.propFirmTemplateId);
  if (a.bufferStatus?.isViolation && !a.isActive) return "breached";
  if (!a.isActive || a.status === "inactive") return "inactive";
  if (a.status === "challenge") return "challenge";
  if (a.status === "pa") return "funded";
  if (a.status === "demo") return "demo";
  return hasFirm ? "live" : "personal";
}

function toLiteAccount(a: AccountLike, charges: Charge[]): LiteAccount {
  const tpl = a.effectiveTemplate ?? a.group?.propFirmTemplate ?? null;
  const firm: LiteFirm | null = tpl ? { key: tpl.id, id: tpl.id, slug: tpl.slug, name: tpl.name, color: tpl.color, textColor: tpl.textColor } : null;
  const payouts = (a.payouts ?? []).map(p => ({ id: p.id, date: String(p.date), amount: p.amount }));
  const payoutsTotal = payouts.reduce((s, p) => s + p.amount, 0);
  const since = a.createdAt ? new Date(a.createdAt) : new Date();
  const expensesTotal = accountExpensesInPeriod(a, null, null)
    + charges.filter(c => c.accountId === a.id).reduce((s, c) => s + chargeAmountInPeriod(c, since, null, null), 0);

  let target: LiteAccount["target"] = null;
  if (a.status === "challenge" && a.challengeProgress?.targetAmount) {
    target = { kind: "challenge", ratio: Math.max(0, Math.min(1, a.challengeProgress.ratio)), amount: a.challengeProgress.targetAmount, current: a.challengeProgress.profit };
  } else if (a.payoutProgress?.targetAmount) {
    target = { kind: "payout", ratio: Math.max(0, Math.min(1, a.payoutProgress.ratio)), amount: a.payoutProgress.targetAmount, current: a.payoutProgress.currentProfit };
  }

  // Même plancher que la carte de compte Advanced (resolveFloor) : pas de
  // plancher durable (règle absente ou journalière) = pas de jauge, plutôt
  // qu'une marge inventée. La jauge mesure la distance au plancher rapportée
  // à celle du point de départ.
  const b = a.bufferStatus;
  const floor = resolveFloor(b as Parameters<typeof resolveFloor>[0], a.initialBalance);
  const start = a.activeTier?.accountSize ?? a.initialBalance;
  // Trailing : rapportée au drawdown du palier, car le plancher d'un funded
  // figé (initial + 100) est au-dessus du point de départ.
  const cushionMax = b?.ruleType === "trailing_drawdown" ? b.maxAllowedLoss : start - (floor ?? start);
  const cushion = b && floor !== null && cushionMax > 0
    ? { remaining: a.currentBalance - floor, ratio: Math.max(0, Math.min(1, (a.currentBalance - floor) / cushionMax)), max: cushionMax, warning: b.isWarning, violation: b.isViolation }
    : null;

  // Mêmes bornes que la carte de compte Advanced : une seule barre plancher -> cible.
  const bounds = resolveBounds({
    status: a.status,
    initialBalance: a.initialBalance,
    objectiveBase: start,
    buffer: b ?? null,
    challengeTargetAmount: a.challengeProgress?.targetAmount ?? null,
    payoutTargetAmount: a.payoutProgress?.targetAmount ?? null,
  });
  const axis = bounds ? { bounds, tone: (b?.isViolation ? "violation" : b?.isWarning ? "warning" : "normal") as AxisTone } : null;

  return {
    id: a.id,
    name: a.name,
    raw: a,
    firm,
    firmKey: firm?.key ?? "personal",
    status: toStatus(a),
    active: a.isActive,
    balance: a.currentBalance,
    // Même définition que la carte Advanced : solde courant moins solde de départ.
    pnl: a.currentBalance - a.initialBalance,
    target,
    cushion,
    axis,
    payouts,
    payoutsTotal,
    expensesTotal,
    net: payoutsTotal - expensesTotal,
  };
}

/**
 * Un compte appartient-il au groupe choisi ? Challenge / funded suivent la même
 * règle que le PFM (passage en funded daté ou payout encaissé = funded), pour
 * qu'un compte clôturé reste rangé dans sa phase.
 */
export function inAccountSet(a: LiteAccount, set: AccountSet): boolean {
  if (set === "all") return true;
  if (set === "active") return a.active && a.raw.status !== "inactive";
  return matchesPhase(a.raw, set);
}

interface LiteData {
  loading: boolean;
  fmt: LiteFormat;
  trades: LiteTrade[];
  /** Groupe de comptes effectif : choix de l'utilisateur, sinon défaut de la page. */
  accountSet: AccountSet;
  /** Compte retenu par les filtres compte / groupe / prop firm (la stratégie ne filtre que les trades). */
  inScope: (a: LiteAccount, opts?: { ignorePhase?: boolean }) => boolean;
  /** Filtrés par compte / prop firm / stratégie (sans période). */
  scopedTrades: LiteTrade[];
  /** Filtrés par tous les filtres, période comprise. */
  filteredTrades: LiteTrade[];
  accounts: LiteAccount[];
  accountsById: Map<string, LiteAccount>;
  rawAccounts: AccountLike[];
  hiddenAccountsCount: number;
  firms: LiteFirm[];
  strategies: { id: string; name: string; color: string }[];
  tags: { id: string; name: string; color?: string | null }[];
  charges: Charge[];
  tptWallet: { propFirmTemplateId: string; accounts: { id: string; name: string; currency: string; status: string }[] } | null;
  moodByDay: Map<string, MoodKey>;
  filters: LiteFilters;
  setFilters: (patch: Partial<LiteFilters>) => void;
  resetFilters: () => void;
  refetchAll: () => void;
}

const Ctx = createContext<LiteData | null>(null);

export function LiteDataProvider({ children }: { children: ReactNode }) {
  const { language } = useLanguageContext();
  const fmt = useMemo(() => makeFormat(language), [language]);

  const tradesQ = useQuery(getAllTradesForAnalytics, { excludeInactive: false });
  const accountsQ = useQuery(getAccountsByUser);
  const strategiesQ = useQuery(getTradingStrategiesByUser);
  const chargesQ = useQuery(getAccountChargesByUser);
  const walletQ = useQuery(getTptWalletContext);
  const sessionsQ = useQuery(getTradingSessionsByUser);
  const tagsQ = useQuery(getTradeTagsByUser);

  const [filters, setFiltersState] = useState<LiteFilters>(readFilters);

  const charges = useMemo(() => ((chargesQ.data ?? []) as unknown as Charge[]), [chargesQ.data]);
  const rawAccounts = useMemo(() => (((accountsQ.data as any)?.accounts ?? []) as AccountLike[]), [accountsQ.data]);
  const accounts = useMemo(() => rawAccounts.map(a => toLiteAccount(a, charges)), [rawAccounts, charges]);
  const accountsById = useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  const firmOf = useMemo(() => new Map(accounts.map(a => [a.id, a.firmKey])), [accounts]);
  const firms = useMemo(() => {
    const m = new Map<string, LiteFirm>();
    for (const a of accounts) if (a.firm) m.set(a.firm.key, a.firm);
    return [...m.values()].sort((x, y) => x.name.localeCompare(y.name));
  }, [accounts]);

  const trades = useMemo(
    () => ((tradesQ.data ?? []) as any[]).map(toLiteTrade).sort((a, b) => b.openedAt.localeCompare(a.openedAt)),
    [tradesQ.data],
  );
  const { pathname } = useLocation();
  const accountSet: AccountSet = filters.accountSet ?? defaultAccountSet(pathname);
  const inScope = useCallback((a: LiteAccount, opts?: { ignorePhase?: boolean }) =>
    (!filters.accountId || a.id === filters.accountId)
    && (opts?.ignorePhase && accountSet !== "active" ? true : inAccountSet(a, accountSet))
    && (!filters.firm || a.firmKey === filters.firm),
  [filters.accountId, accountSet, filters.firm]);
  const scopeIds = useMemo(
    () => (filters.accountId || filters.firm || accountSet !== "all" ? new Set(accounts.filter(a => inScope(a)).map(a => a.id)) : null),
    [accounts, inScope, filters.accountId, filters.firm, accountSet],
  );
  const scopedTrades = useMemo(() => trades.filter(t => matchScope(t, filters, scopeIds)), [trades, filters, scopeIds]);
  const filteredTrades = useMemo(() => scopedTrades.filter(t => inPeriod(t.openedAt, filters.period)), [scopedTrades, filters.period]);

  const moodByDay = useMemo(() => {
    const m = new Map<string, MoodKey>();
    for (const s of (sessionsQ.data ?? []) as any[]) {
      const when = s.startedAt ?? s.date;
      if (s.mood && when) m.set(dayKey(new Date(when)), s.mood as MoodKey);
    }
    return m;
  }, [sessionsQ.data]);

  const setFilters = useCallback((patch: Partial<LiteFilters>) => {
    setFiltersState(f => {
      const next = { ...f, ...patch };
      // Un compte qui n'appartient pas à la prop firm choisie n'a plus de sens.
      if (patch.firm !== undefined && next.firm && next.accountId && firmOf.get(next.accountId) !== next.firm) next.accountId = null;
      try { sessionStorage.setItem(FILTERS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [firmOf]);

  const resetFilters = useCallback(() => setFilters({ accountId: null, accountSet: null, firm: null, strategyId: null }), [setFilters]);

  const refetchAll = useCallback(() => {
    tradesQ.refetch(); accountsQ.refetch(); chargesQ.refetch(); walletQ.refetch(); tagsQ.refetch(); sessionsQ.refetch();
  }, [tradesQ, accountsQ, chargesQ, walletQ, tagsQ, sessionsQ]);

  const value: LiteData = {
    loading: tradesQ.isLoading || accountsQ.isLoading,
    fmt,
    accountSet, inScope,
    trades, scopedTrades, filteredTrades,
    accounts, accountsById, rawAccounts,
    hiddenAccountsCount: (accountsQ.data as any)?.hiddenAccountsCount ?? 0,
    firms,
    strategies: ((strategiesQ.data ?? []) as any[]).map(s => ({ id: s.id, name: s.name, color: s.color ?? "#00C9A7" })),
    tags: ((tagsQ.data ?? []) as any[]).map(t => ({ id: t.id, name: t.name, color: t.color })),
    charges,
    tptWallet: (walletQ.data as LiteData["tptWallet"]) ?? null,
    moodByDay,
    filters, setFilters, resetFilters,
    refetchAll,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLiteData() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLiteData hors LiteDataProvider");
  return v;
}
